import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { API, Table, Card } from '@enkla-gui'
import { date, notify } from '@/_helpers'
import { core } from '@config'
import ReturnModal from './ReturnModal'

export default function LeaseQuittances({ lease, onUnreturnedCountChange }) {
	const history = useHistory()
	const [quittances, setQuittances] = useState([])
	const [loading, setLoading] = useState(true)
	const [returnModal, setReturnModal] = useState(null)
	const [sorting, setSorting] = useState({})

	useEffect(() => {
		if (lease.id) {
			loadQuittances()
		}
	}, [lease.id])

	useEffect(() => {
		const unreturnedCount = quittances.filter(q => !q.returned_at && !q.return_date).length
		if (onUnreturnedCountChange) {
			onUnreturnedCountChange(unreturnedCount)
		}
	}, [quittances, onUnreturnedCountChange])

	const loadQuittances = async () => {
		if (!lease.id) return

		setLoading(true)
		try {
			const quittancesResponse = await API.get(`api/quittance/lease/${lease.id}`)
			setQuittances(quittancesResponse || [])
		} catch (error) {
			notify.toast('Kunde inte ladda kvittenser', 'error')
		}
		setLoading(false)
	}

	const handleReturn = (quittance) => {
		setReturnModal(quittance)
	}

	const handleReturnSuccess = () => {
		setReturnModal(null)
		loadQuittances()
	}

	const handlePDF = quittance => {
		if (!quittance?.id) return
		const link = document.createElement('a')
		link.href = `${core.server}api/quittance/pdf/${quittance.id}`
		link.target = '_blank'
		link.rel = 'noopener noreferrer'
		link.click()
	}

	const getTypeLabel = (type) => {
		const typeMap = {
			'nyckel': 'Nyckel',
			'tag': 'Tag/Kort',
			'fjärrkontroll': 'Fjärrkontroll',
			'annat': 'Annat'
		}
		return typeMap[type] || type
	}

	const getReturnDate = (quittance) => {
		const returnTimestamp = quittance.returned_at || quittance.return_date
		return returnTimestamp ? date.short(returnTimestamp) : ''
	}

	const isReturned = (quittance) => {
		return !!(quittance.returned_at || quittance.return_date)
	}

	const handleStatusClick = (quittance) => {
		// Only allow return action if not already returned
		if (!isReturned(quittance)) {
			handleReturn(quittance)
		}
	}



	return (
		<>
			<Card
				title='Kvittenser'
				icon='fas fa-key'
				collapsed={quittances?.length === 0}
				buttons={{
					header: [{
						callback: () => lease.id && history.push(`/lease/${lease.id}/quittance/create`),
						text: 'Lägg till',
						icon: 'fas fa-plus-square',
						hidden: lease?.state?.status === 'inactive'
					}]
				}}
			>
				<Table
					rows={quittances}
					fetching={loading}
					empty={{ text: 'Det finns inte några kvittenser', icon: 'fas fa-key' }}
					sorting={sorting}
					useSorting={sort => setSorting({ ...sort })}
					functions={{
						type: type => getTypeLabel(type),
						handout_date: timestamp => date.short(timestamp),
						return_date: quittance => getReturnDate(quittance) || '-',
						status: quittance => isReturned(quittance) ?
							<span className='badge bg-success'><i className='fas fa-check'></i> Återlämnad</span> :
							<span className='badge bg-warning' style={{ cursor: 'pointer' }} onClick={() => handleStatusClick(quittance)}>
								<i className='fas fa-clock'></i> Aktiv - Klicka för att återlämna
							</span>,
						pdf: quittance => handlePDF(quittance)
					}}
					keys={[
						'name',
						'quantity',
						'type>function:type',
						'marking',
						'handout_date>function:handout_date',
						'description',
						'_this>function:return_date',
						'_this>function:status',
						'_this>button:pdf:fa-file-pdf'
					]}
					headers={[
						{ key: 'name', value: 'Namn', sortable: true },
						{ key: 'quantity', value: 'Antal', class: 'min', sortable: true },
						{ key: 'type', value: 'Typ', class: 'min', sortable: true },
						{ key: 'marking', value: 'Märkning', sortable: true },
						{ key: 'handout_date', value: 'Utlämnad', class: 'min', sortable: true },
						{ key: 'description', value: 'Beskrivning', sortable: true },
						{ key: 'return_date', value: 'Återlämnad', class: 'min', sortable: true },
						{ value: 'Status', class: 'min' },
						{ value: 'PDF', class: 'min text-end' }
					]}
				>
				</Table>
			</Card>

			{
				returnModal && (
					<ReturnModal
						quittance={returnModal}
						onClose={() => setReturnModal(null)}
						onSuccess={handleReturnSuccess}
					/>
				)
			}
		</>
	)
}
